import React, {useEffect, useState} from "react";
import axios from "axios";
import ReportTable from "./common/ReportTable";
import TableSkeleton from "../utils/TableSkeleton";
import EmptyTableState from "../utils/EmptyTableState";
import * as Constants from "../utils/Constants";
import "../styles/components/reports.css";

import {
  formatDateToDDMMYYYY,
  getAuthToken,
  getClientIdOfUser,
  getGeolocationFromLatLng,
} from "../utils/utility";

import {toLocalISO} from "../utils/dateUtils";
import {extractList, extractCount, describeSchema} from "../utils/responsePayload";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import {CardList} from "./CardView";
import {customStyles} from "../utils/DataTableCustomStyle";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faBan,
  faBolt,
  faFileExcel,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";

const Gt06TrackerReport = () => {
  const TODAY = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(TODAY);
  const [endDate, setEndDate] = useState(TODAY);
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [assetList, setAssetList] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/";
  };

  const columns = [
    {
      name: "Date-Time", id: "Date-Time",
      selector: (row) => row.dateTime,
      sortable: true,
      grow: 1.8,
      minWidth: "155px",
      cell: (row) => (row.dateTime ? toLocalISO(row.dateTime) : "N/A"),
    },
    {
      name: "Asset", id: "Asset",
      selector: (row) => row.assetName,
      sortable: true,
      grow: 2,
      minWidth: "130px",
    },
    {
      name: "Speed", id: "Speed",
      selector: (row) => row.speed,
      sortable: true,
      grow: 0.8,
      minWidth: "80px",
      cell: (row) => (row.speed != null ? `${row.speed} km/h` : "—"),
    },
    {
      name: "Engine", id: "Engine",
      selector: (row) => row.accOn,
      sortable: true,
      grow: 0.8,
      minWidth: "80px",
      cell: (row) => (
        <span className={`lock-status-badge ${row.accOn ? "badge-online" : "badge-offline"}`}>
          <FontAwesomeIcon icon={row.accOn ? faBolt : faBan} />
          {row.accOn ? "ON" : "OFF"}
        </span>
      ),
    },
  ];

  const ExpandedRow = ({data: row}) => {
    const alarms = [
      row.powerCutAlarm && "Power Cut",
      row.vibrationAlarm && "Vibration",
      row.lowBattery && "Low Battery",
    ].filter(Boolean);
    const fields = [
      ["IMEI", row.deviceId],
      ["Course", row.course != null ? `${row.course}°` : "—"],
      ["GPS Fix", row.gpsFix ? "Valid" : "Invalid"],
      ["Voltage", row.voltageLevel != null ? String(row.voltageLevel) : "—"],
      ...(alarms.length ? [["Alarms", alarms.join(", ")]] : []),
    ];
    return (
      <div style={{display: "flex", flexWrap: "wrap", padding: "10px 20px 12px", background: "#fafbff", borderBottom: "2px solid #e9ecef", borderLeft: "4px solid #FF683A"}}>
        {fields.map(([label, value], i, arr) => (
          <div key={label} style={{display: "flex", flexDirection: "column", gap: "3px", padding: "2px 20px 2px 0", marginRight: "20px", borderRight: i < arr.length - 1 ? "1px solid #e5e7eb" : "none"}}>
            <span style={{fontSize: "0.62rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em"}}>{label}</span>
            <span style={{fontSize: "0.84rem", fontWeight: 500, color: alarms.length && label === "Alarms" ? "#dc2626" : "#1f2937"}}>{value}</span>
          </div>
        ))}
      </div>
    );
  };

  const onSearchClick = () => {
    if (
      Object.keys(checkedItems).filter((id) => checkedItems[id]).length === 0 &&
      (!startDate || !endDate)
    ) {
      alert("Select Asset and/or Date Range.");
      return;
    }
    setCurrentPage(1);
    handleSearch(1, perPage);
  };

  const handleSearch = async (page = 1, size = perPage) => {
    setIsLoading(true);
    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${getAuthToken()}`;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const checkedAssetIds = Object.keys(checkedItems).filter((id) => checkedItems[id]);

      const params = new URLSearchParams();
      if (checkedAssetIds.length > 0) params.set("assetIds", checkedAssetIds.join(","));
      if (startDate && endDate) {
        params.set("startDate", startDate);
        params.set("endDate", endDate);
      }
      params.set("timezone", timezone);
      params.set("page", page - 1);
      params.set("size", size);

      const apiUrl = `api/v1/${getClientIdOfUser()}/gt06-location-reports?${params.toString()}`;
      const response = await axios.get(Constants.BASE_URL + apiUrl);

      if (response.status === 200) {
        const pageData = response.data;
        // Defensive unwrap — tolerate Page {content}, envelope {responseData}
        // or raw-array shapes so a payload change never blanks the table.
        const rows = extractList(
          pageData,
          ["content", "responseData", "data", "devices"],
          "gt06-location-reports response"
        );
        let data = rows.map((r) => ({
          ...r,
          deviceId: r.assetUnitId,
        }));
        data = await updateLocationForReports(data);
        data = enrichAssetNames(assetList, data);
        setReports(data);
        // Count always derives from real data: server total when present,
        // otherwise the length of what we actually parsed.
        const total = extractCount(
          pageData,
          ["totalElements", "total_elements", "totalRows", "deviceCount", "device_count"],
          data
        );
        setTotalRows(total);
        console.info(
          `GPS Tracker reports loaded. Found ${data.length} rows on this page (total ${total}).`
        );
        if (!data.length) {
          console.debug(
            "GPS Tracker report page came back empty. Raw payload schema:",
            describeSchema(pageData)
          );
        }
      } else if (response.status === 403) {
        handleLogout();
      }
    } catch (err) {
      console.error(err);
      console.debug(
        "GPS Tracker report fetch failed. Raw payload schema:",
        describeSchema(err?.response?.data)
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    handleSearch(page, perPage);
  };

  const handlePerRowsChange = (newSize, page) => {
    setPerPage(newSize);
    setCurrentPage(page);
    handleSearch(page, newSize);
  };

  const enrichAssetNames = (assets, reportRows) => {
    return reportRows.map((r) => {
      const asset = assets.find((a) => a.assetUnitId === r.deviceId);
      return {...r, assetName: asset ? asset.name : r.deviceId};
    });
  };

  async function updateLocationForReports(reportRows) {
    return Promise.all(
      reportRows.map(async (report) => {
        if (report.latitude && report.longitude) {
          const loc = await getGeolocationFromLatLng(report.latitude, report.longitude);
          return {...report, location: loc || "Location not available"};
        }
        return report;
      })
    );
  }

  const fetchData = async () => {
    try {
      const endPoint = `${Constants.API_V1}/${getClientIdOfUser()}/${Constants.EP_ASSETS_GET_ALL}`;
      axios.defaults.headers.common["Authorization"] = `Bearer ${getAuthToken()}`;
      const res = await axios.get(`${Constants.BASE_URL}${endPoint}`);
      if (res.status === 200) {
        // Defensive unwrap — tolerate {responseData}, {data}, {devices},
        // nested or snake_case envelopes, and raw arrays.
        const allAssets = extractList(
          res.data,
          ["responseData", "data", "devices", "content"],
          "getAllAssets response"
        );
        const trackers = allAssets.filter(
          (a) => (a.assetType || "").toUpperCase() === "GT06_TRACKER"
        );
        console.info(`GPS Tracker reports loaded. Found ${trackers.length} devices.`);
        if (!trackers.length) {
          console.debug(
            "GT06 asset list resolved empty. Raw payload schema:",
            describeSchema(res.data)
          );
        }
        return trackers;
      }
    } catch (err) {
      console.error("Error fetching GT06 assets:", err);
      console.debug(
        "GT06 asset fetch failed. Raw payload schema:",
        describeSchema(err?.response?.data)
      );
    }
    return [];
  };

  const exportToPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");
    const margin = 30;

    const tableData = reports.map((r) => {
      const alerts = [];
      if (r.powerCutAlarm) alerts.push("Power Cut");
      if (r.vibrationAlarm) alerts.push("Vibration");
      if (r.lowBattery) alerts.push("Low Battery");
      return [
        r.dateTime ? toLocalISO(r.dateTime) : "—",
        r.deviceId || "—",
        r.assetName || "—",
        (r.location || "").substring(0, 80),
        r.speed != null ? `${r.speed} km/h` : "—",
        r.course != null ? `${r.course}°` : "—",
        r.accOn ? "ON" : "OFF",
        r.gpsFix ? "Valid" : "Invalid",
        r.voltageLevel != null ? String(r.voltageLevel) : "—",
        alerts.join(", ") || "—",
      ];
    });

    autoTable(doc, {
      startY: 85,
      head: [
        ["Date-Time", "Asset ID", "Asset Name", "Location", "Speed", "Course", "Engine", "GPS Fix", "Voltage", "Alert"],
      ],
      body: tableData,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 6,
        textColor: [51, 65, 85],
        lineColor: [226, 232, 240],
        lineWidth: 0.5,
        valign: "middle",
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [54, 49, 104],
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: "bold",
        halign: "left",
      },
      alternateRowStyles: {fillColor: [248, 250, 252]},
      margin: {top: 85, left: margin, right: margin, bottom: 50},
      didDrawPage: function (data) {
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        try {
          const img = new Image();
          img.src = require("../assets/yekkada-logo-new.png");
          doc.addImage(img, "PNG", margin, 20, 120, 42);
        } catch (e) {
          // ignore missing logo
        }
        doc.setFontSize(12);
        doc.setTextColor(54, 49, 104);
        doc.setFont("helvetica", "normal");
        const title = "GPS Tracker Reports";
        doc.text(title, pageWidth - margin - doc.getTextWidth(title), 55);
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(1);
        doc.line(margin, 60, pageWidth - margin, 60);
        doc.line(margin, pageHeight - 45, pageWidth - margin, pageHeight - 45);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 116, 139);
        doc.text("Page " + doc.internal.getNumberOfPages(), margin, pageHeight - 30);
        const dateStr = "Generated: " + new Date().toLocaleString();
        doc.text(dateStr, pageWidth - margin - doc.getTextWidth(dateStr), pageHeight - 30);
      },
    });

    const now = new Date();
    const ts =
      now.getFullYear().toString() +
      String(now.getMonth() + 1).padStart(2, "0") +
      String(now.getDate()).padStart(2, "0") +
      "_" +
      String(now.getHours()).padStart(2, "0") +
      String(now.getMinutes()).padStart(2, "0") +
      String(now.getSeconds()).padStart(2, "0");
    doc.save(`gt06_tracker_reports_${ts}.pdf`);
  };

  const exportToExcel = () => {
    const excelData = reports.map((r) => {
      const alerts = [];
      if (r.powerCutAlarm) alerts.push("Power Cut");
      if (r.vibrationAlarm) alerts.push("Vibration");
      if (r.lowBattery) alerts.push("Low Battery");
      return {
        "Date-Time": r.dateTime ? toLocalISO(r.dateTime) : "—",
        "Asset ID": r.deviceId || "—",
        "Asset Name": r.assetName || "—",
        Location: r.location || "—",
        "Speed (km/h)": r.speed != null ? r.speed : "—",
        "Course (deg)": r.course != null ? r.course : "—",
        Engine: r.accOn ? "ON" : "OFF",
        "GPS Fix": r.gpsFix ? "Valid" : "Invalid",
        Voltage: r.voltageLevel != null ? r.voltageLevel : "—",
        Alert: alerts.join(", ") || "—",
      };
    });

    const ws = XLSX.utils.json_to_sheet(excelData);
    ws["!cols"] = [
      {wch: 22}, {wch: 15}, {wch: 20}, {wch: 50},
      {wch: 12}, {wch: 12}, {wch: 10}, {wch: 10}, {wch: 10}, {wch: 20},
    ];
    const wb = {Sheets: {data: ws}, SheetNames: ["data"]};
    const excelBuffer = XLSX.write(wb, {bookType: "xlsx", type: "array"});
    const now = new Date();
    const ts =
      now.getFullYear().toString() +
      String(now.getMonth() + 1).padStart(2, "0") +
      String(now.getDate()).padStart(2, "0") +
      "_" +
      String(now.getHours()).padStart(2, "0") +
      String(now.getMinutes()).padStart(2, "0") +
      String(now.getSeconds()).padStart(2, "0");
    FileSaver.saveAs(new Blob([excelBuffer]), `gt06_tracker_reports_${ts}.xlsx`);
  };

  const handleClear = () => {
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setEndDate(today);
    setReports([]);
    setCheckedItems({});
    setTotalRows(0);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchData().then((data) => setAssetList(data || []));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckboxToggle = (assetUnitId) => {
    setCheckedItems((prev) => ({...prev, [assetUnitId]: !prev[assetUnitId]}));
  };

  const paginationComponentOptions = {
    rowsPerPageText: "Rows per page:",
    rangeSeparatorText: "of",
  };

  return (
    <div className="report-content-split">
      <div className="report-main-area">
        <div className="report-header-actions">
          <span className="report-results-title">
            GPS Tracker Results {totalRows > 0 && `(${totalRows})`}
          </span>
          {totalRows > 0 && (
            <div className="export-buttons">
              <button className="export-btn" onClick={exportToExcel}>
                <FontAwesomeIcon icon={faFileExcel} /> Excel
              </button>
              <button className="export-btn" onClick={exportToPDF}>
                <FontAwesomeIcon icon={faFilePdf} /> PDF
              </button>
            </div>
          )}
        </div>

        <div className="report-table-wrapper">
          <ReportTable
            columns={columns}
            data={reports}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationDefaultPage={currentPage}
            paginationPerPage={perPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            paginationRowsPerPageOptions={[25, 50, 100]}
            paginationComponentOptions={paginationComponentOptions}
            customStyles={customStyles}
            progressPending={isLoading}
            progressComponent={<TableSkeleton />}
            fixedHeader={true}
            fixedHeaderScrollHeight="100%"
            noDataComponent={<EmptyTableState variant="search" />}
            expandableRows
            expandableRowsComponent={ExpandedRow}
          />
        </div>
      </div>

      <div className="report-filter-sidebar">
        <div className="sidebar-header">
          <h3 className="sidebar-title">Search Filters</h3>
        </div>

        <div className="sidebar-content">
          <div className="filter-group">
            <label className="filter-label">
              Select GPS Trackers{assetList.length > 0 && ` (${assetList.length})`}
            </label>
            <div className="asset-list-wrapper">
              <CardList
                data={assetList}
                checkedItems={checkedItems}
                toggleCheckbox={handleCheckboxToggle}
              />
            </div>
          </div>
          <div>
            <label className="filter-label">Start Date</label>
            <input
              type="date"
              className="filter-input"
              value={startDate}
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="filter-label">End Date</label>
            <input
              type="date"
              className="filter-input"
              value={endDate}
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="sidebar-footer">
          <div className="filter-actions">
            <button className="btn-report" onClick={onSearchClick}>
              {Constants.FETCH_REPORTS}
            </button>
            <button className="btn-report secondary" onClick={handleClear}>
              {Constants.CLEAR_FILTERS}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Gt06TrackerReport;
